import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/lib/i18n";
import { api } from "@/lib/api";
import LanguageToggle from "@/components/LanguageToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Anchor, ArrowRight, CheckCircle2, UserPlus } from "lucide-react";

const HERO_BG = "https://images.pexels.com/photos/30710213/pexels-photo-30710213.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Login() {
  const { user, login } = useAuth();
  const t = useT();
  const navigate = useNavigate();
  const [mode, setMode] = useState("signin"); // signin | signup | signup-success | forgot | forgot-success
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user !== null) return <Navigate to="/app/dashboard" replace />;

  const submitSignIn = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/app/dashboard");
    else setErr(res.error || "Login failed");
  };

  const submitSignUp = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await api.post("/auth/register", { email, password, name, phone: phone || null });
      setMode("signup-success");
    } catch (ex) {
      const detail = ex.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await api.post("/auth/password-reset/request", { email });
      setMode("forgot-success");
    } catch (ex) {
      const detail = ex.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Request failed");
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setErr("");
    setEmail(""); setPassword(""); setName(""); setPhone("");
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2 bg-[#050A14]">
      {/* Left hero */}
      <div className="relative hidden lg:block overflow-hidden">
        <img src={HERO_BG} alt="Yacht on calm waters" className="absolute inset-0 w-full h-full object-cover opacity-80" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#050A14]/60 via-[#050A14]/70 to-[#050A14]" />
        <div className="relative z-10 h-full flex flex-col justify-between p-10 xl:p-14">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-md bg-[#007AFF]/15 border border-[#007AFF]/40 flex items-center justify-center">
              <Anchor className="w-6 h-6 text-[#4DA6FF]" />
            </div>
            <div>
              <div className="font-display text-lg font-black tracking-tight">LASOVITA LEDGER</div>
              <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{t("login.operationalSystem")}</div>
            </div>
          </div>
          <div className="max-w-lg">
            <h1 className="font-display text-4xl xl:text-5xl font-black tracking-tight leading-tight mb-4">
              {t("login.tagline1")}<br/>{t("login.tagline2")}<br/>{t("login.tagline3")}<br/>
              <span className="text-[#4DA6FF]">{t("login.tagline4")}</span>
            </h1>
            <p className="text-sm text-[#94A3B8] leading-relaxed max-w-md">
              {t("login.description")}
            </p>
          </div>
          <div className="flex items-center gap-6 text-[10px] font-mono tracking-widest text-[#94A3B8]">
            <span>/ ROK KOBAL 50%</span>
            <span>/ BORUT KRZIC 50%</span>
            <span>/ JUN–SEP SEASON</span>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-10 relative">
        <div className="absolute top-4 right-4 z-10"><LanguageToggle compact /></div>
        <div className="w-full max-w-md" data-testid="login-form">
          {mode === "signup-success" ? (
            <div className="text-center space-y-4" data-testid="signup-success">
              <div className="w-14 h-14 rounded-full bg-[#10B981]/15 border border-[#10B981]/40 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-7 h-7 text-[#34D399]" />
              </div>
              <h2 className="font-display text-2xl font-bold tracking-tight">{t("login.signUpSuccess").split(".")[0]}.</h2>
              <p className="text-sm text-[#94A3B8]">{t("login.signUpSuccess")}</p>
              <Button onClick={() => switchMode("signin")} data-testid="back-to-signin-btn"
                className="bg-[#0B1221] border border-[#1E293B] hover:border-[#4DA6FF]/50 hover:bg-[#131C31]">
                {t("login.backToSignIn")}
              </Button>
            </div>
          ) : mode === "forgot-success" ? (
            <div className="text-center space-y-4" data-testid="forgot-success">
              <div className="w-14 h-14 rounded-full bg-[#10B981]/15 border border-[#10B981]/40 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-7 h-7 text-[#34D399]" />
              </div>
              <h2 className="font-display text-2xl font-bold tracking-tight">{t("login.forgotSuccessTitle")}</h2>
              <p className="text-sm text-[#94A3B8]">{t("login.forgotSuccessBody")}</p>
              <Button onClick={() => switchMode("signin")} data-testid="forgot-back-to-signin"
                className="bg-[#0B1221] border border-[#1E293B] hover:border-[#4DA6FF]/50 hover:bg-[#131C31]">
                {t("login.backToSignIn")}
              </Button>
            </div>
          ) : mode === "forgot" ? (
            <>
              <div className="mb-6">
                <div className="font-mono text-[11px] tracking-widest text-[#4DA6FF] mb-3">{t("login.forgotLabel")}</div>
                <h2 className="font-display text-2xl font-bold tracking-tight mb-2">{t("login.forgotTitle")}</h2>
                <p className="text-xs text-[#94A3B8]">{t("login.forgotSub")}</p>
              </div>
              <form onSubmit={submitForgot} className="space-y-4">
                <div>
                  <Label htmlFor="forgot-email" className="text-xs font-mono tracking-widest text-[#94A3B8]">{t("login.email")}</Label>
                  <Input
                    id="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email" required data-testid="forgot-email-input"
                    className="mt-1.5 bg-[#0B1221] border-[#1E293B] text-[#F8FAFC] focus-visible:ring-[#007AFF] focus-visible:border-[#007AFF]"
                  />
                </div>
                {err && (
                  <div className="text-xs text-[#F87171] font-mono bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-md p-3" data-testid="forgot-error">
                    {err}
                  </div>
                )}
                <Button type="submit" disabled={loading} data-testid="forgot-submit-btn"
                  className="w-full bg-[#007AFF] hover:bg-[#0056B3] text-white font-medium tracking-wide">
                  {loading ? t("login.forgotSubmitting") : t("login.forgotSubmit")}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <div className="text-center">
                  <button type="button" onClick={() => switchMode("signin")} data-testid="forgot-back-link"
                    className="text-xs text-[#94A3B8] hover:text-[#4DA6FF]">
                    {t("login.backToSignIn")}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <>
              <div className="mb-6">
                <div className="font-mono text-[11px] tracking-widest text-[#4DA6FF] mb-3">{t("login.restricted")}</div>
                {mode === "signup" ? (
                  <>
                    <h2 className="font-display text-2xl font-bold tracking-tight mb-2">{t("login.signUpTitle")}</h2>
                    <p className="text-xs text-[#94A3B8]">{t("login.signUpSub")}</p>
                  </>
                ) : (
                  <>
                    <h2 className="font-display text-3xl font-bold tracking-tight mb-2">{t("login.signInToContinue")}</h2>
                    <p className="text-sm text-[#94A3B8]">{t("login.authorisedOnly")}</p>
                  </>
                )}
              </div>

              {/* Tabs */}
              <div className="flex gap-1 mb-6 p-1 bg-[#0B1221] border border-[#1E293B] rounded-md" data-testid="login-mode-tabs">
                <button type="button" onClick={() => switchMode("signin")}
                  data-testid="tab-signin"
                  className={`flex-1 px-3 py-2 rounded text-xs font-medium transition-colors ${mode === "signin" ? "bg-[#007AFF]/15 text-[#F8FAFC] border border-[#007AFF]/40" : "text-[#94A3B8] border border-transparent hover:text-[#F8FAFC]"}`}>
                  {t("login.signInTab")}
                </button>
                <button type="button" onClick={() => switchMode("signup")}
                  data-testid="tab-signup"
                  className={`flex-1 px-3 py-2 rounded text-xs font-medium transition-colors ${mode === "signup" ? "bg-[#007AFF]/15 text-[#F8FAFC] border border-[#007AFF]/40" : "text-[#94A3B8] border border-transparent hover:text-[#F8FAFC]"}`}>
                  <UserPlus className="w-3 h-3 inline mr-1" /> {t("login.signUpTab")}
                </button>
              </div>

              <form onSubmit={mode === "signin" ? submitSignIn : submitSignUp} className="space-y-4">
                {mode === "signup" && (
                  <div>
                    <Label htmlFor="name" className="text-xs font-mono tracking-widest text-[#94A3B8]">{t("login.fullName")}</Label>
                    <Input
                      id="name" type="text" value={name} onChange={(e) => setName(e.target.value)}
                      required autoComplete="name" data-testid="signup-name-input"
                      className="mt-1.5 bg-[#0B1221] border-[#1E293B] text-[#F8FAFC] focus-visible:ring-[#007AFF] focus-visible:border-[#007AFF]"
                    />
                  </div>
                )}
                <div>
                  <Label htmlFor="email" className="text-xs font-mono tracking-widest text-[#94A3B8]">{t("login.email")}</Label>
                  <Input
                    id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    autoComplete={mode === "signup" ? "email" : "username"} required
                    data-testid={mode === "signup" ? "signup-email-input" : "login-email-input"}
                    className="mt-1.5 bg-[#0B1221] border-[#1E293B] text-[#F8FAFC] focus-visible:ring-[#007AFF] focus-visible:border-[#007AFF]"
                  />
                </div>
                <div>
                  <Label htmlFor="password" className="text-xs font-mono tracking-widest text-[#94A3B8]">{t("login.password")}</Label>
                  <Input
                    id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                    autoComplete={mode === "signup" ? "new-password" : "current-password"} required
                    minLength={mode === "signup" ? 8 : undefined}
                    data-testid={mode === "signup" ? "signup-password-input" : "login-password-input"}
                    className="mt-1.5 bg-[#0B1221] border-[#1E293B] text-[#F8FAFC] focus-visible:ring-[#007AFF] focus-visible:border-[#007AFF]"
                  />
                  {mode === "signup" && <div className="text-[10px] text-[#94A3B8] mt-1 font-mono">{t("login.passwordMin")}</div>}
                  {mode === "signin" && (
                    <div className="text-right mt-1.5">
                      <button type="button" onClick={() => switchMode("forgot")} data-testid="forgot-link"
                        className="text-[11px] text-[#94A3B8] hover:text-[#4DA6FF] font-mono tracking-wide">
                        {t("login.forgotLink")}
                      </button>
                    </div>
                  )}
                </div>
                {mode === "signup" && (
                  <div>
                    <Label htmlFor="phone" className="text-xs font-mono tracking-widest text-[#94A3B8]">{t("login.phone")}</Label>
                    <Input
                      id="phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                      autoComplete="tel" data-testid="signup-phone-input"
                      className="mt-1.5 bg-[#0B1221] border-[#1E293B] text-[#F8FAFC] focus-visible:ring-[#007AFF] focus-visible:border-[#007AFF]"
                    />
                  </div>
                )}

                {err && (
                  <div className="text-xs text-[#F87171] font-mono bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-md p-3" data-testid="login-error">
                    {err}
                  </div>
                )}

                <Button
                  type="submit" disabled={loading}
                  data-testid={mode === "signup" ? "signup-submit-btn" : "login-submit-btn"}
                  className="w-full bg-[#007AFF] hover:bg-[#0056B3] text-white font-medium tracking-wide"
                >
                  {loading
                    ? (mode === "signup" ? t("login.submittingSignUp") : t("login.authenticating"))
                    : (mode === "signup" ? t("login.submitSignUp") : t("login.submit"))}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
